import type { AuditRecorder } from "../audit/audit-recorder.js";
import type { RequestContext } from "../application/context/request-context.js";
import type { AIReviewRepository } from "../repositories/ai-review-repository.js";
import type { AIReviewSubjectType } from "../domain/ai-review/ai-review-model.js";
import { AIReviewOrchestrator } from "../application/services/ai-review-orchestrator.js";
import { buildAIReviewAdapters, type AIReviewAdapterDeps } from "../application/services/ai-review-adapters.js";
import { InspectIaReviewService } from "../application/services/inspect-ia-review-service.js";
import { LocalRulesProvider } from "../infrastructure/ai/local-rules-provider.js";

interface Gated {
  technicalReviewService?: { attachSubmissionGate(gate: (context: RequestContext, documentType: string, documentId: string) => Promise<void>): void };
  reportService?: { attachAiReviewGate(gate: (context: RequestContext, documentId: string) => Promise<void>): void };
  avrReportService?: { attachAiReviewGate(gate: (context: RequestContext, documentId: string) => Promise<void>): void };
}

const GATED_TYPES: ReadonlySet<string> = new Set(["ME", "LP", "PM", "USC", "BSCAN", "AVR", "INTEGRITY"]);

/**
 * Liga o INSPECT IA REVIEW. A mesma função nas duas composições.
 * Só `service` (a fachada) vai para o /api/rpc; o orquestrador e o
 * provedor ficam por trás. Os gates de envio ao PLH são ligados aqui —
 * todos consultam o MESMO orquestrador.
 */
export function wireInspectIaReview(
  repo: AIReviewRepository,
  services: AIReviewAdapterDeps & Gated,
  audit: AuditRecorder | undefined,
  userName?: (userId: string) => Promise<string | null>
): { service: InspectIaReviewService; orchestrator: AIReviewOrchestrator } {
  const local = new LocalRulesProvider();
  const orchestrator = new AIReviewOrchestrator(repo, { local, external: null }, audit, userName);
  for (const adapter of buildAIReviewAdapters(services)) orchestrator.registerAdapter(adapter);
  const service = new InspectIaReviewService(orchestrator, repo, local, audit);

  services.technicalReviewService?.attachSubmissionGate(async (context, documentType, documentId) => {
    if (GATED_TYPES.has(documentType)) await orchestrator.assertReadyForSubmit(context, documentType as AIReviewSubjectType, documentId);
  });
  services.reportService?.attachAiReviewGate((context, id) => orchestrator.assertReadyForSubmit(context, "INTEGRITY", id));
  services.avrReportService?.attachAiReviewGate((context, id) => orchestrator.assertReadyForSubmit(context, "AVR", id));
  return { service, orchestrator };
}
